import { type FC, useState, useEffect, useRef, type MouseEventHandler } from 'react';
import { type Disposable, graphql, useFragment, useMutation } from 'react-relay';

import { useIntl, defineMessages } from 'dibs-react-intl';

import { Link } from 'dibs-elements/exports/Link';
import { Button } from 'dibs-elements/exports/Button';

import { useRouteClickHandler } from '../useRouteClickHandler';
import { NftProfileImportCollectionRetryError } from './NftProfileImportCollectionRetryError';
import {
    NftProfileImportCollectionRetryModal,
    NftProfileImportCollectionRetryToast,
    type NftProfileImportCollectionRetryStatus,
} from './NftProfileImportCollectionRetryIndicators';
import { paths } from '../../../../constants';

import { type NftProfileImportCollectionRetry_viewer$key } from './__generated__/NftProfileImportCollectionRetry_viewer.graphql';
import { type NftProfileImportCollectionRetry_nftItem$key } from './__generated__/NftProfileImportCollectionRetry_nftItem.graphql';

const ctaMessages = defineMessages({
    success: {
        id: 'nftProfile.importCollectionRetry.successCta',
        defaultMessage: 'View Collection',
    },
    tryAgain: { id: 'nftProfile.importCollectionRetry.tryAgainCta', defaultMessage: 'Try Again' },
});

const viewerFragment = graphql`
    fragment NftProfileImportCollectionRetry_viewer on Viewer {
        publicProfile(username: $username) {
            username
        }
        nftTokenContractImportById(tokenContractId: $tokenContractId) {
            serviceId
        }
    }
`;
const nftItemFragment = graphql`
    fragment NftProfileImportCollectionRetry_nftItem on NftItem {
        serviceId
        importErrorReason
    }
`;
const mutation = graphql`
    mutation NftProfileImportCollectionRetryMutation($nftItemIds: [String!]) {
        nftRetryItemImport(input: { nftItemIds: $nftItemIds }) {
            nftItems {
                ...NftProfileImportCollectionRetry_nftItem
            }
        }
    }
`;
export const NftProfileImportCollectionRetry: FC<{
    viewer: NftProfileImportCollectionRetry_viewer$key;
    nftItem: NftProfileImportCollectionRetry_nftItem$key;
    refetchCollection?: () => void;
    setIsRetrying: (arg: boolean) => void;
}> = ({ viewer: viewerRef, nftItem: nftItemRef, setIsRetrying, refetchCollection }) => {
    const { formatMessage } = useIntl();
    const [isModalOpen, setIsModalOpen] = useState<null | boolean>(null);
    const [hasRetried, setHasRetried] = useState(false);
    const viewer = useFragment(viewerFragment, viewerRef);
    const nftItem = useFragment(nftItemFragment, nftItemRef);
    const [commitMutation, retryInFlight] = useMutation(mutation);
    const id = viewer.nftTokenContractImportById?.serviceId;
    const handleRouteClick = useRouteClickHandler(id);
    const collectionUrl = viewer.publicProfile?.username
        ? `${paths.profileUser}${viewer.publicProfile.username}/${id}/`
        : undefined;

    const hasError = !!nftItem.importErrorReason;
    const canRetry =
        !retryInFlight &&
        nftItem.importErrorReason === 'NFT_ITEM_IMPORT_ERROR_REASON_MEDIA_PROCESSING_ERROR';
    const mutationDisposableRef = useRef<Disposable>(undefined);
    let status: NftProfileImportCollectionRetryStatus = null;
    if (retryInFlight) {
        status = 'importing';
    } else if (canRetry) {
        status = 'tryAgain';
    } else if (hasRetried && !hasError) {
        status = 'success';
    }

    const handleRetry = (shouldOpen: boolean): void => {
        if (canRetry) {
            setHasRetried(true);
            setIsRetrying(true);
            if (shouldOpen) {
                setIsModalOpen(true);
            }
            mutationDisposableRef.current = commitMutation({
                variables: { nftItemIds: [nftItem.serviceId] },
                onCompleted: () => setIsRetrying(false),
                onError: () => setIsRetrying(false),
                onUnsubscribe: () => setIsRetrying(false),
            });
        }
    };

    // cleanup mutation on unmount
    useEffect(() => () => mutationDisposableRef.current?.dispose(), []);

    let ctaProps = null;
    if (status === 'tryAgain') {
        ctaProps = {
            getClickHandler:
                (shouldOpen: boolean = true) =>
                () =>
                    handleRetry(shouldOpen),
            children: formatMessage(ctaMessages.tryAgain),
        };
    } else if (status === 'success') {
        ctaProps = {
            getClickHandler: (): MouseEventHandler => e => {
                handleRouteClick(e);
                refetchCollection?.();
            },
            href: collectionUrl,
            children: formatMessage(ctaMessages.success),
        };
    }

    return (
        <>
            {hasError && (
                <NftProfileImportCollectionRetryError
                    errorReason={nftItem.importErrorReason}
                    onRetry={() => handleRetry(true)}
                    canRetry={canRetry}
                />
            )}
            {hasRetried && (
                <NftProfileImportCollectionRetryModal
                    status={status}
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    cta={
                        ctaProps ? (
                            <Button
                                fullWidth
                                children={ctaProps.children}
                                href={ctaProps.href}
                                onClick={ctaProps.getClickHandler(true)}
                            />
                        ) : undefined
                    }
                />
            )}
            {hasRetried && (
                <NftProfileImportCollectionRetryToast
                    status={status}
                    isModalOpen={isModalOpen}
                    cta={
                        ctaProps ? (
                            <Link
                                children={ctaProps.children}
                                href={ctaProps.href}
                                onClick={ctaProps.getClickHandler(false)}
                            />
                        ) : undefined
                    }
                />
            )}
        </>
    );
};
